import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { loadPlaces, placesConfigured, PLACES_COUNTRIES } from '../../lib/googlePlaces';

// How long to wait after typing stops before asking Google. Each request is
// billable, so this is deliberately not per-keystroke.
const DEBOUNCE_MS = 300;
const MIN_CHARS = 3;

/**
 * Address field with Google Places suggestions.
 *
 * Uses the Autocomplete Data API rather than Google's prebuilt widget, so the
 * field keeps the app's own styling. Google closed the legacy
 * `places.Autocomplete` widget to new API projects in March 2025, so this is
 * also the only path available to a newly created key.
 *
 * Degrades to an ordinary free-text field when no API key is configured or the
 * script fails to load — a vendor can always type their address by hand.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  label = 'Store Address',
  placeholder = 'e.g. 123 Main St, Melbourne VIC 3000',
  error,
  helperText,
  required,
  TextFieldComponent = TextField,
  ...textFieldProps
}) {
  const [ready, setReady] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Session tokens group the keystrokes of one lookup with the final place
  // fetch into a single billable session. A new one starts after each pick.
  const sessionToken = useRef(null);
  const placesLib = useRef(null);
  const debounceRef = useRef(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!placesConfigured) return undefined;
    let cancelled = false;

    loadPlaces()
      .then((lib) => {
        if (cancelled) return;
        placesLib.current = lib;
        sessionToken.current = new lib.AutocompleteSessionToken();
        setReady(true);
      })
      .catch((err) => {
        // Not fatal — the field stays usable as plain text.
        console.warn('[places]', err.message);
      });

    return () => { cancelled = true; };
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    const lib = placesLib.current;
    if (!lib || input.trim().length < MIN_CHARS) {
      setOptions([]);
      return;
    }

    const thisRequest = ++requestId.current;
    setLoading(true);
    try {
      const request = {
        input,
        sessionToken: sessionToken.current,
        ...(PLACES_COUNTRIES.length ? { includedRegionCodes: PLACES_COUNTRIES } : {}),
      };
      const { suggestions } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      // Ignore responses that arrived out of order.
      if (thisRequest !== requestId.current) return;

      setOptions(
        (suggestions || [])
          .map((s) => s.placePrediction)
          .filter(Boolean)
          .map((p) => ({ label: p.text?.toString() || '', prediction: p }))
          .filter((o) => o.label)
      );
    } catch (err) {
      console.warn('[places] suggestion lookup failed:', err.message);
      if (thisRequest === requestId.current) setOptions([]);
    } finally {
      if (thisRequest === requestId.current) setLoading(false);
    }
  }, []);

  const handleInputChange = (_event, newInput, reason) => {
    // 'reset' fires when a value is selected — don't re-query for it.
    if (reason === 'reset') return;
    onChange(newInput);

    if (!ready) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(newInput), DEBOUNCE_MS);
  };

  const handleSelect = async (_event, option) => {
    if (!option) return;

    // A typed string (freeSolo) rather than a suggestion.
    if (typeof option === 'string') { onChange(option); return; }

    try {
      const place = option.prediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress'] });
      onChange(place.formattedAddress || option.label);
    } catch (err) {
      console.warn('[places] place details failed:', err.message);
      onChange(option.label);
    } finally {
      // The session is spent; start a fresh one for the next lookup.
      const lib = placesLib.current;
      if (lib) sessionToken.current = new lib.AutocompleteSessionToken();
      setOptions([]);
    }
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return (
    <Autocomplete
      freeSolo
      disableClearable
      options={options}
      inputValue={value || ''}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      loading={loading}
      // Google ranks the results; don't let MUI re-filter them.
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      isOptionEqualToValue={(o, v) => o.label === (typeof v === 'string' ? v : v?.label)}
      noOptionsText="No matching addresses"
      renderInput={(params) => (
        <TextFieldComponent
          {...params}
          {...textFieldProps}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
